import React, { useEffect, useState } from "react";
import { Typography, Box, Button } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AppleIcon from "@mui/icons-material/Apple";
import GoogleIcon from "@mui/icons-material/Google";
import { formatTimeRange } from "../../utils/FormatTime";
import { getTranslations } from "../../utils/CardTranslations";
import { fetchWeather, resolveWeatherCode } from "../../utils/weatherUtils";
import { CloudSun, Thermometer } from 'lucide-react'
import { format12Hour } from "../../utils/FormatTime";


// ─── Weather Widget ───────────────────────────────────────────────────────────

// ─── Color Helper ─────────────────────────────────────────────────────────────
const getBaseColor = (color) => {
    if (!color || typeof color !== "string") return "#68D2D2";
    return color.startsWith("#") ? color.slice(0, 7) : color;
};

// ─── Recommendation logic ─────────────────────────────────────────────────────
const getRecommendation = (weatherCode, precipProb, t) => {
    if (weatherCode >= 80 || (weatherCode >= 61 && weatherCode <= 67))
        return { icon: "☂️", text: t.bringUmbrella };
    if (weatherCode >= 51 && weatherCode <= 57)
        return { icon: "☂️", text: t.bringUmbrella };
    if (precipProb != null && precipProb >= 40)
        return { icon: "☂️", text: t.bringUmbrella };
    return { icon: "", text: "" };
};

export const WeatherWidget = ({ weather, themeColour, loading, startTime, language }) => {
    const t = getTranslations(language);
    const baseColor = getBaseColor(themeColour);

    if (loading) {
        return (
            <Box
                sx={{
                    mt: 1.5,
                    mb: 0.5,
                    px: 1.5,
                    py: 1.2,
                    borderRadius: "12px",
                    background: `${baseColor}14`,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                {[60, 140, 100, 80].map((w, i) => (
                    <Box
                        key={i}
                        sx={{
                            height: 12,
                            width: w,
                            borderRadius: "6px",
                            background: "linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)",
                            backgroundSize: "200% 100%",
                            animation: "weatherShimmer 1.4s infinite",
                            "@keyframes weatherShimmer": {
                                "0%": { backgroundPosition: "200% 0" },
                                "100%": { backgroundPosition: "-200% 0" },
                            },
                        }}
                    />
                ))}
            </Box>
        );
    }

    if (!weather || weather.status === "error" || weather.status === "too-far") return null;

    // status === "available"
    const rec = getRecommendation(weather.weatherCode, weather.precipitationProbability, t);
    const resolvedWeather = resolveWeatherCode(weather.weatherCode, language);

    // Temperature range bar — dot reflects actual temperature at the event's start hour
    const { tempMin, tempMax, temperature } = weather;
    const dotPct =
        tempMax !== tempMin && temperature != null
            ? Math.min(100, Math.max(0, ((temperature - tempMin) / (tempMax - tempMin)) * 100))
            : 50; // fallback: midpoint

    return (
        <Box
            sx={{
                mt: 1.5,
                mb: 0.5,
                p: 2,
                borderRadius: "12px",
                background: `${baseColor}12`,
            }}
        >
            {/* ── Header: icon + title ─────────────────────────────────── */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <CloudSun size={20} color={themeColour} />
                <Typography sx={{ fontSize: 14 }}>
                    {t.weatherTitle}
                </Typography>
            </Box>

            {/* ── Main: emoji + description + probability + recommendation ─ */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, justifyContent: "center" }}>
                {/* Large weather emoji */}
                <Typography sx={{ fontSize: 34, lineHeight: 1, flexShrink: 0 }}>
                    {weather.emoji}
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {/* Weather description + actual event-time temperature */}
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.8 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                            {resolvedWeather.description}
                        </Typography>

                    </Box>

                    {/* Precipitation probability */}
                    {weather.precipitationProbability != null && (
                        <Typography sx={{ fontSize: 11, color: "#666" }}>
                            {weather.precipitationProbability}% {t.rainChance}
                        </Typography>
                    )}

                    {/* Recommendation pill — only for rainy conditions */}
                    {rec.text && (
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                mt: 0.5,
                                px: 1,
                                py: 0.3,
                                borderRadius: "20px",
                                background: `${baseColor}20`,
                                width: "fit-content",
                            }}
                        >
                            <Typography sx={{ fontSize: 11, lineHeight: 1 }}>{rec.icon}</Typography>
                            <Typography sx={{ fontSize: 10, color: themeColour }}>
                                {t.recommendationPrefix}: {rec.text}
                            </Typography>
                        </Box>
                    )}

                </Box>
            </Box>

            {/* ── Divider ──────────────────────────────────────────────── */}
            <Box sx={{ height: "1px", background: "#e0e0e0", mx: 1, mt: 2, mb: 2 }} />

            {/* ── Min / Max temperature ─────────────────────────────────── */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                {/* Minimum */}
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1.2 }}>
                    <Box
                        sx={{
                            width: 32, height: 32, borderRadius: "50%",
                            background: "#e8eaf6",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <Thermometer size={18} color="#5c6bc0" />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 9, color: "#9e9e9e", letterSpacing: "0.8px" }}>
                            {t.tempMinimum}
                        </Typography>
                        <Typography sx={{ fontSize: 18, color: "#5c6bc0", lineHeight: 1.1 }}>
                            {weather.tempMin}°C
                        </Typography>
                    </Box>
                </Box>

                {/* Vertical divider */}
                <Box sx={{ width: "1px", height: 44, background: "#e0e0e0", mx: 1 }} />

                {/* Maximum */}
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1.2 }}>
                    <Box
                        sx={{
                            width: 32, height: 32, borderRadius: "50%",
                            background: "#fce4ec",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <Thermometer size={18} color="#c62828" />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 9, color: "#9e9e9e", letterSpacing: "0.8px" }}>
                            {t.tempMaximum}
                        </Typography>
                        <Typography sx={{ fontSize: 18, color: "#c62828", lineHeight: 1.1 }}>
                            {weather.tempMax}°C
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* ── Temperature range bar ─────────────────────────────────── */}
            <Box sx={{ position: "relative", mx: 0.5 }}>
                {/* Gradient track */}
                <Box
                    sx={{
                        height: 6,
                        borderRadius: "99px",
                        background: "linear-gradient(to right, #5c6bc0, #c62828)",
                    }}
                />
                {/* Average temp dot */}
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: `${dotPct}%`,
                        transform: "translate(-50%, -50%)",
                        width: 16, height: 16,
                        borderRadius: "50%",
                        background: "#fff",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                >
                    {/* Inner coloured core */}
                    <Box sx={{
                        width: 8, height: 8, borderRadius: "50%",
                        background: `linear-gradient(135deg, #5c6bc0, #c62828)`,
                    }} />
                </Box>
            </Box>
            {/* Min / Max labels under the bar */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.8, px: 0.5 }}>
                <Typography sx={{ fontSize: 10, color: "#5c6bc0", fontWeight: 600 }}>
                    {tempMin}°C
                </Typography>
                <Typography sx={{ fontSize: 10, color: "#9e9e9e", fontWeight: 500, textAlign: "center" }}>
                    {temperature != null ? `${temperature}°C` : t.tempRange}
                    {startTime && temperature != null && (
                        <Box component="span" sx={{ color: "#9e9e9e", fontWeight: 400 }}>
                            {" "}:{" "}{format12Hour(startTime)}
                        </Box>
                    )}
                </Typography>
                <Typography sx={{ fontSize: 10, color: "#c62828", fontWeight: 600 }}>
                    {tempMax}°C
                </Typography>
            </Box>
        </Box>
    );
};



// ─── Main Component ───────────────────────────────────────────────────────────

const EventCalendar = ({ data, previewMode = false }) => {

    const eventTitle = data?.eventType;
    const eventLocation = data?.eventAddress;
    const eventDate = data?.eventDate;
    const greetingName = data?.greetingName;
    const t = getTranslations(data?.language);

    // ── Location coordinates from data.location (same pattern as EventLocation.jsx) ──
    const location = data?.location;
    const lat = location?.lat ?? null;
    const lng = location?.lng ?? null;

    // ── Weather state ──
    const [weather, setWeather] = useState(null);
    const [weatherLoading, setWeatherLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const loadWeather = async () => {
            setWeatherLoading(true);
            setWeather(null);

            if (lat == null || lng == null || !eventDate) {
                setWeatherLoading(false);
                return;
            }

            // Normalise eventDate to "YYYY-MM-DD" (strips time part if ISO string)
            const dateStr =
                typeof eventDate === "string" && eventDate.includes("-")
                    ? eventDate.split("T")[0]
                    : new Date(eventDate).toISOString().split("T")[0];

            // Open-Meteo uses "longitude" — pass lng and startTime for hourly precision
            const result = await fetchWeather(lat, lng, dateStr, data.startTime);
            if (!cancelled) {
                setWeather(result);
                setWeatherLoading(false);
            }
        };

        loadWeather();

        return () => {
            cancelled = true;
        };
    }, [lat, lng, eventDate, data?.startTime]);

    const formatDateTimeForICS = (dateStr, timeStr) => {
        const [hours, minutes] = timeStr.split(":").map(Number);
        const d = new Date(dateStr);
        d.setHours(hours, minutes, 0, 0); // local time
        const pad = (n) => n.toString().padStart(2, "0");

        // Convert to UTC format for ICS
        return (
            d.getUTCFullYear() +
            pad(d.getUTCMonth() + 1) +
            pad(d.getUTCDate()) +
            "T" +
            pad(d.getUTCHours()) +
            pad(d.getUTCMinutes()) +
            pad(d.getUTCSeconds()) +
            "Z"
        );
    };

    const startICS = formatDateTimeForICS(eventDate, data.startTime);
    const endICS = formatDateTimeForICS(eventDate, data.endTime);

    // Google Calendar link
    const googleCalendarUrl = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
        eventTitle + " " + greetingName.replace(/\n/g, " ")
    )}&dates=${startICS}/${endICS}&details=${encodeURIComponent(
        t.calendarDescription + " " + eventTitle + " " + greetingName.replace(/\n/g, " ")
    )}&location=${encodeURIComponent(eventLocation)}&sf=true&output=xml`;




    const handleAddToAppleCalendar = () => {

        const icsContent = [
            "BEGIN:VCALENDAR",
            "VERSION:2.0",
            "BEGIN:VEVENT",
            `SUMMARY:${eventTitle} ${greetingName.replace(/\n/g, " ")}`,
            `DTSTART:${startICS}`,
            `DTEND:${endICS}`,
            `LOCATION:${eventLocation}`,
            `DESCRIPTION:${t.calendarDescription} ${eventTitle} ${greetingName.replace(/\n/g, " ")}`,
            "END:VEVENT",
            "END:VCALENDAR"
        ].join("\r\n");

        const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${eventTitle.replace(/\s+/g, "_")}.ics`;
        a.click();
        URL.revokeObjectURL(url);
    };


    const buttonStyle = {
        textTransform: "none",
        border: "none",
        backgroundColor: data?.buttonColour || data?.themeColour,
        borderRadius: "50px",
        fontSize: "12px",
        color: "white",
        px: "10px",
        py: "10px",
        "&:hover": {
            boxShadow: "0 8px 16px 0 rgba(0,0,0,0.2)",
            transition: "0.4s",
        },
    };

    return (
        <div
            style={{
                fontFamily: data.fontStyle || "Poppins, sans-serif",
                textAlign: "start",
                width: "300px",
                margin: "auto",
            }}
        >
            <Box
                sx={{
                    mt: 0,
                    px: 2,
                    pt: 3,
                    pb: 3,
                    border: "1px solid #ddd",
                    borderRadius: "20px",
                    backdropFilter: "blur(3px)",
                    background: "rgba(255, 255, 255, 0.85)",
                }}
            >

                {/* Display Event Date */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        background: `${getBaseColor(data?.buttonColour || data?.themeColour)}12`,
                        borderRadius: "12px",
                        p: 2,
                        mb: 0,
                    }}
                >
                    <CalendarMonthIcon sx={{ color: data?.buttonColour || data?.themeColour, mr: 1 }} />
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {new Date(eventDate).toLocaleDateString(t.calendarLocale, {
                                weekday: "long",
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                            })}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "gray" }}>
                            {formatTimeRange(data.startTime, data.endTime)}
                        </Typography>
                    </Box>
                </Box>

                {/* Weather Forecast Widget — hidden when weatherForecast is explicitly false */}
                {data.weatherForecast !== false && (
                    <WeatherWidget
                        weather={weather}
                        themeColour={data?.buttonColour || data?.themeColour}
                        loading={weatherLoading}
                        startTime={data.startTime}
                        language={data.language}
                    />
                )}

                {/* Buttons to Save */}

                <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<GoogleIcon />}
                    sx={{ ...buttonStyle, mt: 2, ...(previewMode && { pointerEvents: "none", cursor: "default" }) }}
                    onClick={!previewMode ? () => window.open(googleCalendarUrl, "_blank") : undefined}
                >
                    Google Calendar
                </Button>

                <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<AppleIcon />}
                    sx={{ ...buttonStyle, mt: 1.5, ...(previewMode && { pointerEvents: "none", cursor: "default" }) }}
                    onClick={!previewMode ? handleAddToAppleCalendar : undefined}
                >
                    Apple Calendar
                </Button>

            </Box>
        </div>
    );
};

export default EventCalendar;
